import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { Icon, Select, Spin } from 'antd';
import _ from 'lodash';

const { Option } = Select;

class ExtendedSelect extends React.Component {
  constructor(props) {
    super(props);
    this.lastFetchId = 0;

    this.state = {
      data: [],
      value: this.getInitialValue(),
      fetching: false
    };

    this.handleSearch = this.handleSearch.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSearch = debounce(this.handleSearch, 800);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    // Set once the focus handler has loaded the unfiltered list - see handleFocus.
    this.focusLoaded = false;
  }

  componentDidMount() {
    const { dataSource, value, fetching } = this.props;
    this.loadDataSource(dataSource, value, fetching);
  }

  getInitialValue() {
    const { mode, value } = this.props;

    return value || (mode === 'multiple' || mode === 'tags' ? [] : undefined);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { dataSource, fetching, value } = nextProps;
    const { value: prevValue } = this.props;
    const { value: curValue } = this.state;

    const valueChanged = !_.isEqual(prevValue, value) && !_.isEqual(curValue, value);

    if (valueChanged) {
      // -- Value has changed. Update the user
      // -- selected value and refresh the
      // -- list datasource.
      // ---
      this.setState({ value });
      this.loadDataSource(dataSource, value);
    }
    else if (_.isArray(dataSource)) {
      this.loadLocalData(dataSource, curValue, fetching);
    }
  }

  loadDataSource(dataSource, selectedValue, fetching) {
    if (_.isFunction(dataSource)) {
      this.loadRemoteData(dataSource, selectedValue);
    }
    else if (_.isArray(dataSource)) {
      this.loadLocalData(dataSource, selectedValue, fetching);
    }
  }

  loadRemoteData(dataSource, selectedValue, searchText = '') {
    this.lastFetchId += 1;
    const fetchId = this.lastFetchId;

    // ------ Fetch the data ------
    this.setState({
      data: [],
      fetching: true
    });
    dataSource(searchText)
      .then(
        (response) => {
          // ------ Manage the fetch callback order ------
          if (fetchId !== this.lastFetchId) {
            return;
          }

          this.setState({
            data: this.updateData(response, selectedValue),
            fetching: false
          });
        },
        () => {
          this.setState({
            data: this.updateData([], selectedValue),
            fetching: false
          });
        }
      );
  }

  loadLocalData(dataSource = [], selectedValue, fetching = false) {
    this.setState({
      data: this.updateData(fetching ? [] : dataSource, fetching ? undefined : selectedValue),
      fetching
    });
  }

  updateData(data = [], selectedValues) {
    const { optionKey, optionLabel } = this.props;

    // ------ Map the data to Key/Label pairs ------
    const dataSet = data.map(item => ({
      key: optionKey(item),
      label: optionLabel(item)
    }));

    // -- Check if the selected values exists in the
    // -- data array and add the item if it is missing.
    // -- The selected value can be missing from the
    // -- array if the item has been deleted or the
    // -- dataset has been filtered.
    // ---

    if (selectedValues) {
      let valuesToCheck = [];

      if (_.isArray(selectedValues)) {
        valuesToCheck = selectedValues;
      }
      else if (_.isObject(selectedValues)) {
        valuesToCheck.push(selectedValues);
      }

      // -- Add the missing items to the top of the array
      // ---
      const notInList = _.differenceBy(valuesToCheck, dataSet, 'key');
      if (notInList.length > 0) {
        dataSet.splice(0, 0, ...notInList);
      }
    }

    return dataSet;
  }

  handleSearch(searchText) {
    const { dataSource, onSearch } = this.props;
    const { value } = this.state;

    // A typed search narrows the list the parent holds, so the next focus must reload the
    // unfiltered set - see handleFocus.
    this.focusLoaded = false;

    if (_.isFunction(dataSource)) {
      // -- Reload the datasource based on the
      // -- search text.
      // ---
      this.loadRemoteData(dataSource, value, searchText.trim());
      onSearch(searchText);
    }
    else if (_.isArray(dataSource)) {
      // -- If the datasouce is any array, clear
      // -- the dataSource and pass value back
      // -- to the parent prop.
      // ---
      onSearch(searchText);
    }
  }

  handleChange(value) {
    const { dataSource, onChange } = this.props;
    if (value === undefined) {
      // -- Reload the remote datasource if value
      // -- has been cleared or is undefined
      // ---
      if (_.isFunction(dataSource)) {
        this.loadRemoteData(dataSource, value);
      }
    }

    // -- Set the selected value and pass
    // -- value back to the parent prop.
    // ---
    this.setState({ value },
      () => {
        onChange(value);
      });
  }

  handleBlur() {
    const { onBlur } = this.props;
    onBlur();
  }

  // Every call site wires `onFocus={loadX}`, meaning "load the unfiltered list when the field
  // is focused". Two problems with letting that reach antd directly:
  //
  //  1. rc-select forwards the native focus SyntheticEvent to `onFocus`, so `loadX(event)` ran
  //     with the event as its `searchText` - each loader does `if (!_.isEmpty(searchText))` and
  //     an event object is not empty, so a junk `name__icontains` filter was sent. Invoking
  //     `onFocus()` with no arguments is what every loader (`loadX(searchText = '')`) is
  //     written for.
  //  2. It re-requested the same list on *every* focus. Tab out and back in and the identical
  //     GET fires again. Once the unfiltered list has been loaded there is nothing new to
  //     fetch, so skip it until a typed search narrows the list (handleSearch clears the flag).
  handleFocus() {
    const { onFocus } = this.props;
    if (!_.isFunction(onFocus) || this.focusLoaded) {
      return;
    }
    this.focusLoaded = true;
    onFocus();
  }

  render() {
    const {
      placeholder,
      size,
      mode,
      disabled,
      allowClear,
      showSearch,
      maxTagCount,
      dropdownClassName,
      dropdownMatchSelectWidth,
      labelInValue
    } = this.props;
    const { data, value, fetching } = this.state;
    return (
      <Select
        {...this.props}
        style={{ width: '100%' }}
        placeholder={placeholder}
        value={value}
        size={size}
        mode={mode}
        disabled={disabled}
        allowClear={allowClear}
        showSearch={showSearch}
        maxTagCount={maxTagCount}
        defaultActiveFirstOption={false}
        dropdownClassName={dropdownClassName}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        notFoundContent={
          fetching ? (
            <Spin
              spinning
              indicator={<Icon type="loading" spin />}
              size="small"
              tip="Searching"
              direction="horizontal"
            />
          ) : null
        }
        labelInValue={labelInValue}
        filterOption={false}
        onSearch={this.handleSearch}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        // Must come after the {...this.props} spread so the caller's raw `onFocus={loadX}`
        // goes through handleFocus rather than straight to rc-select.
        onFocus={this.handleFocus}
      >
        {data.map(item => (
          <Option key={item.key} value={item.key}>
            {item.label}
          </Option>
        ))}
      </Select>
    );
  }
}

ExtendedSelect.propTypes = {
  placeholder: PropTypes.string,
  dataSource: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // Ant provides this (getFieldDecorator), and it cant have a default, nor be required
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.any, // Value must of a single object or object array of {key,label}
  size: PropTypes.string,
  mode: PropTypes.string,
  disabled: PropTypes.bool,
  fetching: PropTypes.bool,
  allowClear: PropTypes.bool,
  showSearch: PropTypes.bool,
  maxTagCount: PropTypes.number,
  dropdownClassName: PropTypes.string,
  dropdownMatchSelectWidth: PropTypes.bool,
  optionKey: PropTypes.func, // Function that defines the key value to use as the option key. Prop is required if dataSource is a function
  optionLabel: PropTypes.func, // Function that defines the label to use as the option label. Prop is required if dataSource is a function
  onSearch: PropTypes.func, // Returns the serach value. Hook up to this callback if the dataSource is an array.
  onChange: PropTypes.func, // Returns the selected value
  onBlur: PropTypes.func,
  labelInValue: PropTypes.bool
};

ExtendedSelect.defaultProps = {
  placeholder: null,
  dataSource: [],
  size: 'default',
  mode: 'default',
  disabled: false,
  fetching: false,
  allowClear: true,
  showSearch: true,
  maxTagCount: 10,
  dropdownClassName: null,
  dropdownMatchSelectWidth: true,
  optionKey() {
    return '';
  },
  optionLabel() {
    return '';
  },
  onSearch() {
  },
  onChange() {
  },
  onBlur() {
  },
  labelInValue: true
};

export default ExtendedSelect;
